import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  Webhook,
  Plus,
  Trash2,
  Pencil,
  Copy,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useWebhooks } from "hooks/useWebhooks";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useToast } from "components/Toast";
import ConfirmDialog from "components/ConfirmDialog";
import Skeleton from "components/Skeleton";

const WEBHOOK_EVENTS = [
  "application.submitted",
  "application.approved",
  "application.rejected",
  "application.updated",
];

export default function WebhooksTab() {
  const { user } = useAuth();
  const isAdmin = isInternal(user);
  const { webhooks, loading, refetch } = useWebhooks();
  const { toast } = useToast();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [urlInput, setUrlInput] = useState("");
  const [partnerIdInput, setPartnerIdInput] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([...WEBHOOK_EVENTS]);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });

  // Delivery history
  const [expandedId, setExpandedId] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  const toggleEvent = (event) => {
    setSelectedEvents((prev) =>
      prev.includes(event)
        ? prev.filter((e) => e !== event)
        : [...prev, event]
    );
  };

  const openCreate = () => {
    setEditingId(null);
    setUrlInput("");
    setPartnerIdInput("");
    setSelectedEvents([...WEBHOOK_EVENTS]);
    setCreatedSecret(null);
    setShowModal(true);
  };

  const openEdit = (webhook) => {
    setEditingId(webhook._id);
    setUrlInput(webhook.url);
    setSelectedEvents([...(webhook.events || [])]);
    setCreatedSecret(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setCreatedSecret(null);
    refetch();
  };

  const handleSubmit = async () => {
    if (!urlInput.trim()) {
      toast({ status: "destructive", title: "URL is required" });
      return;
    }
    if (selectedEvents.length === 0) {
      toast({
        status: "destructive",
        title: "Select at least one event",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        // Update
        await makeApiRequest(
          "put",
          apiRoutes.updateWebhook(editingId),
          { id: user.id, accessToken: user.accessToken },
          user.companyId,
          { url: urlInput.trim(), events: selectedEvents }
        );
        toast({ status: "success", title: "Webhook updated" });
        closeModal();
      } else {
        // Create
        const body = {
          url: urlInput.trim(),
          events: selectedEvents,
        };
        if (isAdmin && partnerIdInput.trim()) {
          body.partnerId = partnerIdInput.trim();
        }

        const res = await makeApiRequest(
          "post",
          apiRoutes.createWebhook,
          { id: user.id, accessToken: user.accessToken },
          user.companyId,
          body
        );
        setCreatedSecret(res.data?.secret || res.data?.data?.secret);
      }
    } catch (err) {
      toast({
        status: "destructive",
        title: editingId
          ? "Failed to update webhook"
          : "Failed to create webhook",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = (webhookId) => {
    setConfirmDialog({
      open: true,
      title: "Disable webhook",
      description: "Disable this webhook? It will stop receiving events.",
      confirmLabel: "Disable",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest(
            "delete",
            apiRoutes.disableWebhook(webhookId),
            { id: user.id, accessToken: user.accessToken },
            user.companyId
          );
          toast({ status: "success", title: "Webhook disabled" });
          refetch();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to disable webhook",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  const toggleDeliveries = async (webhookId) => {
    if (expandedId === webhookId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(webhookId);
    setLoadingDeliveries(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getWebhookDeliveries(webhookId),
        { id: user.id, accessToken: user.accessToken },
        user.companyId
      );
      setDeliveries(res.data?.data ?? res.data ?? []);
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to load deliveries",
      });
      setDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    toast({ status: "success", title: "Copied to clipboard" });
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-2/3" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {webhooks.length} webhook{webhooks.length !== 1 ? "s" : ""}
        </h3>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Add Webhook
        </button>
      </div>

      {/* List */}
      {webhooks.length > 0 ? (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <div
              key={wh._id}
              className="surface rounded-xl ring-1 ring-border overflow-hidden"
            >
              <div className="flex items-center gap-3 px-4 py-3">
                {/* Expand toggle */}
                <button
                  className="btn btn-ghost btn-xs p-0"
                  onClick={() => toggleDeliveries(wh._id)}
                >
                  {expandedId === wh._id ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>

                {/* URL */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono truncate">{wh.url}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(wh.events || []).map((e) => (
                      <span
                        key={e}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand/10 text-brand"
                      >
                        {e}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Partner (admin) */}
                {isAdmin && (
                  <span className="text-xs text-muted-foreground">
                    {wh.partnerId}
                  </span>
                )}

                {/* Status */}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    wh.status === "active"
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  }`}
                >
                  {wh.status}
                </span>

                {/* Created */}
                <span className="text-xs text-muted-foreground hidden sm:block">
                  {new Date(wh.createdAt).toLocaleDateString()}
                </span>

                {/* Actions */}
                {wh.status === "active" && (
                  <div className="flex gap-1">
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => openEdit(wh)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      className="btn btn-ghost btn-xs text-destructive"
                      onClick={() => handleDisable(wh._id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Delivery History */}
              {expandedId === wh._id && (
                <div className="border-t border-border px-4 py-3 bg-muted/30">
                  <h4 className="text-xs font-medium text-muted-foreground mb-2">
                    Recent Deliveries
                  </h4>
                  {loadingDeliveries ? (
                    <Skeleton.Rect className="h-6 w-full" />
                  ) : deliveries.length > 0 ? (
                    <div className="space-y-1">
                      {deliveries.map((d, i) => (
                        <div
                          key={d._id || i}
                          className="flex items-center gap-3 text-xs"
                        >
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${
                              d.success
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                            }`}
                          >
                            {d.statusCode || "ERR"}
                          </span>
                          <span className="text-muted-foreground">
                            {d.event}
                          </span>
                          <span className="text-muted-foreground">
                            attempt {d.attempt}
                          </span>
                          {d.error && (
                            <span className="text-destructive truncate">
                              {d.error}
                            </span>
                          )}
                          <span className="ml-auto text-muted-foreground">
                            {new Date(d.deliveredAt).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No deliveries yet
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="surface rounded-xl ring-1 ring-border p-8 text-center">
          <Webhook className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No webhooks configured. Add one to receive event notifications.
          </p>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        {...confirmDialog}
        onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))}
      />

      {/* Create/Edit Modal — rendered via portal (SOUND-239) for the same
          SettingsLayout stacking-context reason as TeamTab + ApiKeysTab. */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg">
            {createdSecret ? (
              /* Secret Created View */
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Webhook Created</h3>
                <p className="text-sm text-muted-foreground">
                  Use this secret to verify webhook signatures:
                </p>
                <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                  <code className="flex-1 text-xs break-all font-mono">
                    {createdSecret}
                  </code>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => copyToClipboard(createdSecret)}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This secret will not be shown again. Store it securely.
                  </span>
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={closeModal}
                >
                  Done
                </button>
              </div>
            ) : (
              /* Form */
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  {editingId ? "Edit Webhook" : "Add Webhook"}
                </h3>

                {isAdmin && !editingId && (
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">
                      Partner ID
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="e.g. acme-corp"
                      value={partnerIdInput}
                      onChange={(e) => setPartnerIdInput(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">
                    Endpoint URL
                  </label>
                  <input
                    type="url"
                    className="input input-bordered w-full"
                    placeholder="https://your-server.com/webhooks/sound"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Events
                  </label>
                  <div className="space-y-2">
                    {WEBHOOK_EVENTS.map((event) => (
                      <label
                        key={event}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedEvents.includes(event)}
                          onChange={() => toggleEvent(event)}
                          className="rounded border-border"
                        />
                        <span className="font-mono text-xs">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    className="btn btn-ghost flex-1"
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : editingId
                      ? "Save Changes"
                      : "Create Webhook"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
